import React, {useState} from 'react'
import {SearchIcon, CloseIcon, BrandLogo} from '../../icons'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage, defineMessages} from 'react-intl'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {
    Text,
    Stack,
    HStack,
    Modal,
    ModalHeader,
    ModalBody,
    ModalContent,
    ModalOverlay,
    useMultiStyleConfig,
    useDisclosure,
    IconButton,
    Button,
    Box,
    Grid,
} from '@chakra-ui/react'
import Search from '../index'
import SearchModalProducts from './search-modal-products'
import routingObject from '../../../utils/static-routes'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import SanitizedHtml from '../../html-sanitizer'
import useProductRegistrationHandler from '../../../custom-hooks/use-product-registration-handler'

const SearchModal = () => {
    const iconStyles = useMultiStyleConfig('Header')
    const searchStyles = useMultiStyleConfig('Search', {})
    const {isOpen, onOpen, onClose} = useDisclosure()
    const navigate = useNavigation()
    const {locale} = useMultiSite()
    const handleProductRegistrationClick = useProductRegistrationHandler(routingObject)
    const {formatMessage} = useIntl()
    const buttonData = defineMessages({
        productRegistration: {
            id: 'search.modal.productRegistration',
            defaultMessage: 'Product registration',
        },
        instructionManuals: {
            id: 'search.modal.instructionManuals',
            defaultMessage: 'Instruction manuals',
        },
        authorizedServiceCenters: {
            id: 'search.modal.authorizedServiceCenters',
            defaultMessage: 'Authorized service centers',
        },
    })
    const [searchResults, setSearchResults] = useState([])
    // we dont want to show PDP only products in the search results only the search suggestions
    const filteredSearchResults =
        searchResults?.hits?.length > 0
            ? {
                  ...searchResults,
                  hits: searchResults?.hits?.filter(
                      (product) =>
                          product?.representedProduct?.c_skuIsPDPOnly === 'false' ||
                          product?.representedProduct?.c_isPDPOnlyPurchasable === 'true',
                  ),
              }
            : searchResults
    const [categorySuggestions, setCategorySuggestions] = useState([])
    const [modalNavigation, setModalNavigation] = useState([])
    const [showCategories, setShowCategories] = useState(true)

    const handleClose = () => {
        setSearchResults([]) // Clear search results
        setCategorySuggestions([]) // Optionally clear category suggestions
        setShowCategories(true) // Reset categories visibility if needed
        onClose() // Call the original onClose function
    }

    const handleLogoClick = () => {
        navigate(`/${locale?.alias}`)
        onClose()
    }

    return (
        <>
            <IconButton
                aria-label={formatMessage({
                    id: 'global.search',
                    defaultMessage: 'Search',
                })}
                icon={
                    <SearchIcon
                        sx={{
                            ...iconStyles.headerIcon,
                        }}
                    />
                }
                variant="unstyled"
                color={'text.default'}
                {...iconStyles.buttonIcons}
                onClick={onOpen}
            />
            <Modal isOpen={isOpen} onClose={handleClose} isCentered scrollBehavior="inside">
                <ModalOverlay {...searchStyles.modalOverlay} />
                <ModalContent {...searchStyles.modalContent}>
                    <ModalHeader {...searchStyles.modalHeader}>
                        <Box
                            as="button"
                            aria-label={formatMessage({
                                id: 'search.modal.aria.brandLogo',
                                defaultMessage: 'Go to homepage',
                            })}
                            display="flex"
                            tabIndex={0}
                            onClick={handleLogoClick}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    handleLogoClick()
                                }
                            }}
                        >
                            <BrandLogo sx={searchStyles.logo} />
                        </Box>
                        <Box
                            as="button"
                            aria-label={formatMessage({
                                id: 'search.modal.aria.closeButton',
                                defaultMessage: 'Close search',
                            })}
                            display="flex"
                            tabIndex={0}
                            onClick={handleClose}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    handleClose()
                                }
                            }}
                            data-testid="close-modal"
                        >
                            <CloseIcon
                                {...searchStyles.closeIcon}
                                role="button"
                                aria-label={formatMessage({
                                    id: 'search.modal.aria.closeButton',
                                    defaultMessage: 'Close search modal',
                                })}
                            />
                        </Box>
                    </ModalHeader>
                    <ModalBody {...searchStyles.modalBody}>
                        <Search
                            searchInputPlaceholder={
                                <FormattedMessage defaultMessage="Search" id="global.search" />
                            }
                            isSearchModal
                            setSearchResults={setSearchResults}
                            setCategorySuggestions={setCategorySuggestions}
                            setModalNavigation={setModalNavigation}
                            setShowCategories={setShowCategories}
                            onCloseModal={handleClose}
                            variant="modalSearch"
                            showSpellingMessage={
                                (filteredSearchResults?.hits === undefined ||
                                    filteredSearchResults?.hits?.length === 0) &&
                                filteredSearchResults.length !== 0
                            }
                        />
                        {filteredSearchResults?.hits?.length > 0 && (
                            <Stack {...searchStyles.productsTitleButton}>
                                <Text {...searchStyles.quickLinksTitle}>
                                    <FormattedMessage
                                        defaultMessage="Products"
                                        id="search.modal.products"
                                    />
                                </Text>
                                <Stack>
                                    {filteredSearchResults?.hits?.length > 0 && (
                                        <Box>
                                            <Grid {...searchStyles.productdGrid}>
                                                {filteredSearchResults.hits
                                                    .slice(0, 4)
                                                    .map((product, index) => (
                                                        <SearchModalProducts
                                                            key={product?.productId}
                                                            product={product}
                                                            index={index}
                                                            onCloseModal={handleClose}
                                                            searchResults={filteredSearchResults}
                                                        />
                                                    ))}
                                            </Grid>
                                        </Box>
                                    )}
                                </Stack>
                                <Button
                                    variant="primary"
                                    w="100%"
                                    onClick={() => {
                                        handleClose()
                                        navigate(modalNavigation)
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            handleClose()
                                            navigate(modalNavigation)
                                        }
                                    }}
                                    tabIndex={0}
                                    aria-label={formatMessage({
                                        id: 'search.modal.aria.seeAllProducts',
                                        defaultMessage: 'See all products',
                                    })}
                                >
                                    <FormattedMessage
                                        defaultMessage="See all"
                                        id="search.modal.seeAll"
                                    />
                                </Button>
                            </Stack>
                        )}
                        {categorySuggestions?.length > 0 && showCategories && (
                            <Stack {...searchStyles.trendingStack}>
                                <Text {...searchStyles.quickLinksTitle}>
                                    <FormattedMessage
                                        defaultMessage="Trending searches"
                                        id="search.modal.trendingSearches"
                                    />
                                </Text>
                                <HStack {...searchStyles.buttonModalStack}>
                                    {categorySuggestions?.map((category, index) => (
                                        <Button
                                            key={index}
                                            variant="secondary"
                                            onClick={() => {
                                                onClose()
                                                navigate(category.link)
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    handleClose()
                                                    navigate(category.link)
                                                }
                                            }}
                                            tabIndex={0}
                                            aria-label={'Search: ' + (category.name || 'category')}
                                        >
                                            <SanitizedHtml
                                                as={Text}
                                                html={category.name}
                                                sanitize={true}
                                            />
                                        </Button>
                                    ))}
                                </HStack>
                            </Stack>
                        )}
                        <Stack {...searchStyles.quickLinksStack}>
                            <Text {...searchStyles.quickLinksTitle}>
                                <FormattedMessage
                                    defaultMessage="Quick Links"
                                    id="search.modal.quickLinks"
                                />
                            </Text>
                            <Stack as="ul" {...searchStyles.buttonModalStack}>
                                {Object.keys(buttonData).map((key) => {
                                    const routeMap = {
                                        productRegistration:
                                            routingObject.PRODUCT_REGISTRATION_PATH,
                                        instructionManuals: routingObject.MANUALS_PATH,
                                        authorizedServiceCenters:
                                            locale?.alias?.toLowerCase() === 'en-gb'
                                                ? '/e/care-and-repair'
                                                : '/authorized-service-center',
                                    }

                                    const handleClick = () => {
                                        onClose()
                                        if (key === 'productRegistration') {
                                            handleProductRegistrationClick()
                                        } else {
                                            navigate(routeMap[key])
                                        }
                                    }

                                    return (
                                        <li key={buttonData[key].id} style={{lineHeight: '0'}}>
                                            <Button
                                                key={buttonData[key].id}
                                                variant="text-button"
                                                onClick={() => handleClick()}
                                                tabIndex={0}
                                                aria-label={`${formatMessage(buttonData[key])}`}
                                            >
                                                <Text>
                                                    <FormattedMessage {...buttonData[key]} />
                                                </Text>
                                            </Button>
                                        </li>
                                    )
                                })}
                            </Stack>
                        </Stack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    )
}
SearchModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
}

export default SearchModal
